/**
 * Page Context — captures contextual data from the current page.
 *
 * Extracts URL, title, Open Graph / meta tags, JSON-LD structured data,
 * and visible text from the main content area. Called before each
 * `user_message` so the backend (and AI agent) knows what page the
 * visitor is looking at.
 *
 * All extraction is pure DOM — zero LLM cost. The result is sent as
 * metadata alongside the message; the LLM only pays for the extra
 * tokens in its context window.
 */

/** Truncation limits to keep payloads small (~5 KB worst case). */
const MAX_VISIBLE_TEXT_CHARS = 2000;
const MAX_STRUCTURED_DATA_CHARS = 4096;

export interface OpenGraphData {
  title?: string;
  description?: string;
  image?: string;
  type?: string;
  site_name?: string;
}

export interface PageContext {
  url: string;
  title: string;
  og?: OpenGraphData;
  structured_data?: Record<string, unknown>[];
  visible_text?: string;
}

// ── Layer 1: URL + Title ───────────────────────────────────────────────────

function captureUrlAndTitle(): Pick<PageContext, 'url' | 'title'> {
  return {
    url: window.location.href,
    title: document.title,
  };
}

// ── Layer 2: Open Graph & meta tags ────────────────────────────────────────

function captureOpenGraph(): OpenGraphData | undefined {
  const get = (property: string): string | undefined =>
    document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`)
      ?.content ||
    document.querySelector<HTMLMetaElement>(`meta[name="${property}"]`)
      ?.content ||
    undefined;

  const og: OpenGraphData = {};
  const title = get('og:title');
  const description = get('og:description') ?? get('description');
  const image = get('og:image');
  const type = get('og:type');
  const siteName = get('og:site_name');

  if (title) og.title = title;
  if (description) og.description = description;
  if (image) og.image = image;
  if (type) og.type = type;
  if (siteName) og.site_name = siteName;

  return Object.keys(og).length > 0 ? og : undefined;
}

// ── Layer 3: JSON-LD structured data ───────────────────────────────────────

function captureStructuredData(): Record<string, unknown>[] | undefined {
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[type="application/ld+json"]',
  );
  if (scripts.length === 0) return undefined;

  const results: Record<string, unknown>[] = [];
  let totalChars = 0;

  for (const script of scripts) {
    try {
      const parsed = JSON.parse(script.textContent ?? '') as Record<
        string,
        unknown
      >;
      const serialized = JSON.stringify(parsed);
      if (totalChars + serialized.length > MAX_STRUCTURED_DATA_CHARS) break;
      totalChars += serialized.length;
      results.push(parsed);
    } catch {
      // Malformed JSON-LD — skip silently.
    }
  }

  return results.length > 0 ? results : undefined;
}

// ── Layer 4: Visible text from main content ────────────────────────────────

const CONTENT_SELECTORS = [
  'main',
  'article',
  '[role="main"]',
  '#content',
  '.content',
  '#main-content',
  '.main-content',
];

function captureVisibleText(): string | undefined {
  let container: Element | null = null;

  for (const selector of CONTENT_SELECTORS) {
    container = document.querySelector(selector);
    if (container) break;
  }

  // Fallback to body, but exclude nav/header/footer/aside
  const raw = container
    ? getElementText(container as HTMLElement)
    : getFallbackText();

  if (!raw) return undefined;

  // Normalize whitespace and truncate
  const cleaned = raw.replace(/\s+/g, ' ').trim();
  if (!cleaned) return undefined;

  return cleaned.length > MAX_VISIBLE_TEXT_CHARS
    ? cleaned.slice(0, MAX_VISIBLE_TEXT_CHARS)
    : cleaned;
}

/** Prefer innerText (layout-aware) but fall back to textContent (jsdom). */
function getElementText(el: HTMLElement): string {
  return el.innerText ?? el.textContent ?? '';
}

function getFallbackText(): string {
  // Clone body, remove noisy elements, get text
  const clone = document.body.cloneNode(true) as HTMLElement;
  const noisy = clone.querySelectorAll(
    'nav, header, footer, aside, script, style, noscript, iframe, svg',
  );
  for (const el of noisy) el.remove();
  return getElementText(clone);
}

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Capture full page context (all 4 layers) every time.
 *
 * Always extracts everything because SPAs render content after page load —
 * by the time the visitor sends a message the DOM has the real content,
 * even if it was empty at socket connect. The cost is ~5ms of DOM parsing,
 * negligible compared to the network round trip.
 */
export function capturePageContext(): PageContext {
  const ctx: PageContext = captureUrlAndTitle();

  const og = captureOpenGraph();
  if (og) ctx.og = og;

  const structured = captureStructuredData();
  if (structured) ctx.structured_data = structured;

  const text = captureVisibleText();
  if (text) ctx.visible_text = text;

  return ctx;
}
